"use client";

/**
 * EngravedText — display-serif text with cream→foil-gold gradient fill,
 * subtle inset/emboss shadow, and a left-to-right wipe reveal.
 *
 * Scene 0 (OpeningHookScene) investiture: niche.name rendered like it's
 * chiselled onto a metal plaque, not typed.
 *
 * Reveal mechanics:
 *   - clip-path inset(0 100% 0 0) → inset(0 0% 0 0) wipe (left→right)
 *   - duration default 0.9s, easing curve [0.22, 1, 0.36, 1]
 *
 * Engraving mechanics:
 *   - background gradient (cream → amber-gold → darker gold)
 *   - -webkit-background-clip: text + transparent fill (Tailwind arbitrary)
 *   - -webkit-text-stroke: thin dark outline (metallic depth)
 *   - drop-shadow: thin dark offset (emboss)
 *
 * Reduced-motion: skips the wipe, renders the gradient fill instantly.
 */

import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

// Cubic-bezier tuple — declared so framer-motion's Easing type accepts it.
const EASE_OUT_EXPO: [number, number, number, number] = [0.22, 1, 0.36, 1];

interface EngravedTextProps {
  /** Text content — rendered as-is, inline-block. */
  text: string;
  /** Seconds to wait before starting wipe. Default 0. */
  delay?: number;
  /** Wipe duration in seconds. Default 0.9. */
  duration?: number;
  /** Extra classes — typically font-size / line-height / tracking. */
  className?: string;
}

const ENGRAVED_STYLES =
  // Diagonal gradient (125deg) — cream upper-left → amber-gold middle →
  // darker gold lower-right. Feels like angled light across metal, not flat.
  "[background-image:linear-gradient(125deg,#fcf3d8_0%,#f0d898_30%,#c89b4f_60%,#8a6a2e_100%)] " +
  "[background-clip:text] [-webkit-background-clip:text] " +
  "[color:transparent] [-webkit-text-fill-color:transparent] " +
  // Thin dark outline — reads as metallic edge.
  "[-webkit-text-stroke:0.4px_rgba(20,14,4,0.35)] " +
  // Drop-shadow — thin dark offset + soft amber bloom behind the letters.
  "[filter:drop-shadow(0_1px_0_rgba(0,0,0,0.55))_drop-shadow(0_0_22px_rgba(216,176,96,0.12))]";

export function EngravedText({
  text,
  delay = 0,
  duration = 0.9,
  className,
}: EngravedTextProps) {
  const reduced = useReducedMotion();

  // Reduced-motion fallback: render immediately, no wipe.
  if (reduced) {
    return (
      <span
        className={cn(
          "inline-block font-heading font-bold",
          ENGRAVED_STYLES,
          className,
        )}
      >
        {text}
      </span>
    );
  }

  return (
    <motion.span
      className={cn(
        "inline-block font-heading font-bold",
        ENGRAVED_STYLES,
        className,
      )}
      style={{ willChange: "clip-path, opacity" }}
      initial={{ clipPath: "inset(0 100% 0 0)", opacity: 0.2 }}
      animate={{ clipPath: "inset(0 0% 0 0)", opacity: 1 }}
      transition={{
        clipPath: { duration, delay, ease: EASE_OUT_EXPO },
        opacity: { duration: 0.25, delay, ease: "easeOut" },
      }}
    >
      {text}
    </motion.span>
  );
}
